import { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { RadioTower } from 'lucide-react';

import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'

import {
	Pagination,
	PaginationContent,
	PaginationItem,
	PaginationNext,
	PaginationPrevious,
} from '@/components/ui/pagination'

import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from '@/components/ui/card'

import { formatUnixTimestamp } from '@/utils/format';
import { useEvents, useNetworkStats } from '@/hooks/useApi';
import { SUI_EXPLORER_BASE_URL } from '@/lib/constants';
import { EventTypeDistributionChart } from '@/components/charts/EventTypeDistributionChart';

const PAGE_SIZE = 20;
const MAX_ENTRIES = 10_000;

const Events = () => {
	const pageLimit = MAX_ENTRIES / PAGE_SIZE;
	const initialLoadCompleted = useRef(false);
	const startingCursor = useRef<any>(null);
	const startingPageData = useRef<any>(null);
	const nextCursor = useRef<any>(null);

	const [cursor, setCursor] = useState<any>(null);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [pageToRender, setPageToRender] = useState<any>({});
	const [prevCursorHistory, setPrevCursorHistory] = useState<Array<string>>([]);

	const { data: eventData } = useEvents(PAGE_SIZE, cursor?.txDigest ?? null, cursor?.eventSeq ?? null);

	useEffect(() => {
		if (!initialLoadCompleted.current && eventData?.cursor) {
			startingCursor.current = eventData.cursor;
			startingPageData.current = eventData;
			initialLoadCompleted.current = true;
		}
		if (eventData?.cursor) nextCursor.current = eventData.cursor;
		setPageToRender(
			startingCursor.current === nextCursor.current ? startingPageData.current : eventData
		);
	}, [eventData]);

	if (!eventData) return <>Loading...</>;

	return (
		<>
			<Pagination className="mb-2 border-2 border-black rounded-sm shadow-[3px_3px_0_0_black] px-2 py-1 bg-[#37C5B3]/60 backdrop-blur-md">
				<PaginationContent>
					<PaginationItem>
						<PaginationPrevious
							className="bg-[#C0E6FF]/10 backdrop-blur-md border-2 border-black text-white rounded-sm hover:bg-[#A56FF1]/40 hover:backdrop-blur-md"
							content={<span className="text-sm cursor-default">Prev</span> as any}
							onClick={() => {
								if (currentPage === 1) return;
								if (prevCursorHistory.length === 1) setCursor(null);

								setPrevCursorHistory((prev) => {
									const temp = [...prev];
									temp.pop();
									if (temp.length) setCursor(temp[temp.length - 1]);
									return temp;
								});
								setCurrentPage((prev) => prev - 1);
							}}
						/>
					</PaginationItem>
					<PaginationItem>
						<PaginationNext
							className="bg-[#C0E6FF]/10 backdrop-blur-md border-2 border-black text-white rounded-sm hover:bg-[#A56FF1]/40 hover:backdrop-blur-md"
							content={<span className="text-sm cursor-default">Next</span> as any}
							onClick={() => {
								if (currentPage === pageLimit || !nextCursor.current) return;
								setCursor(nextCursor.current);
								setPrevCursorHistory((prev) => [...prev, nextCursor.current]);
								setCurrentPage((prev) => prev + 1);
							}}
						/>
					</PaginationItem>
				</PaginationContent>
			</Pagination>

			<div className="rounded-sm border-2 border-black shadow-[4px_4px_0_0_black] overflow-hidden">
				<Table>
					<TableHeader className="bg-[#37C5B3]/60 backdrop-blur-md border-b-2 border-black">
						<TableRow className='hover:bg-[#37C5B3]/60'>
							{['Type', 'Blob Id', 'SUI Object Id', 'Tx Digest', 'Timestamp'].map(label => (
								<TableHead key={label} className="text-xs font-bold text-white uppercase">{label}</TableHead>
							))}
						</TableRow>
					</TableHeader>
					<TableBody>
						{pageToRender?.data?.map((event: any) => (
							<TableRow key={event.blobId + '-' + event.timestampMS} className="bg-[#37C5B3]/30 backdrop-blur-md hover:bg-[#37C5B3]/20">
								<TableCell className="text-md text-bold text-white">{event.type.split('::').pop()}</TableCell>
								<TableCell className="text-md text-bold text-white">
									<Link to={`/blobs/${event.objectId}`} className="underline decoration-2 decoration-[#A56FF1]">
										<span className='hidden md:block text-md text-bold text-white'>{`${event.blobId.slice(0, 6)}...${event.blobId.slice(-6)}`}</span>
										<span className="md:hidden text-white">{`${event.blobId.slice(0, 6)}...`}</span>
									</Link>
								</TableCell>
								<TableCell className="text-md text-bold text-white">
									<Link to={`${SUI_EXPLORER_BASE_URL}/objects/${event.objectId}`} className="underline decoration-2 decoration-[#A56FF1]" target='_blank'>
										<span className='hidden md:block text-md text-bold text-white'>{`${event.objectId.slice(0, 6)}...${event.objectId.slice(-6)}`}</span>
										<span className="md:hidden text-white">{`${event.objectId.slice(0, 6)}...`}</span>
									</Link>
								</TableCell>
								<TableCell className="text-md text-bold text-white">
									<Link to={`${SUI_EXPLORER_BASE_URL}/transaction-blocks/${event.digest}`} className="underline decoration-2 decoration-[#A56FF1]" target='_blank'>
										<span className='hidden md:block text-md text-bold text-white'>{`${event.digest.slice(0, 6)}...${event.digest.slice(-6)}`}</span>
										<span className="md:hidden text-white">{`${event.digest.slice(0, 6)}...`}</span>
									</Link>
								</TableCell>
								<TableCell className="text-md text-bold text-white">
									{formatUnixTimestamp(Number(event.timestampMS))}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
		</>
	);
};


const EventsExplorePage = () => {
	const { data: networkStats } = useNetworkStats()

	const transformedData = useMemo(() => {
		return [
			{
				"EVENT_TYPE": "blobCertified",
				"TOTAL_COUNT": networkStats?.blobCertified
			},
			{
				"EVENT_TYPE": "blobRegistered",
				"TOTAL_COUNT": networkStats?.blobRegistered
			},
			{
				"EVENT_TYPE": "blobDeleted",
				"TOTAL_COUNT": networkStats?.blobDeleted
			}
		]
	}, [networkStats])


	return (
		<>
			<div className="flex-1 overflow-y-auto overflow-x-hidden flex max-w-full [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
				<div className='flex flex-col gap-6 w-full h-full'>
					<div className="flex flex-col justify-center w-full gap-6 pr-8 lg:px-4 py-5">

						<Card className='bg-[#0C0F1D]/70 backdrop-blur-md text-black border-2 border-black rounded-sm shadow-[4px_4px_0_0_black] p-4 transition-colors duration-150'>
							<CardHeader className="p-0 mb-2">
								<div className='flex flex-row items-center justify-between'>
									<div className="flex items-center gap-2">
										<div className="p-1 text-white">
											<RadioTower className="h-5 w-5 text-white" />
										</div>
										<CardTitle className="text-sm font-bold text-white">Event Type Distribution</CardTitle>
									</div>
								</div>
								<CardDescription className='text-xs mt-1 text-white'>
									Distribution of blob related events.
								</CardDescription>
							</CardHeader>

							<CardContent className="p-0 pt-2 h-40">
								<EventTypeDistributionChart data={transformedData} isLoading={false} />
							</CardContent>
						</Card>




						<Card className="bg-[#0C0F1D]/70 backdrop-blur-md border-2 border-black shadow-[4px_4px_0_0_black] rounded-sm">
							<CardHeader className="p-0">
								<div className="flex items-center gap-2 px-2 pt-2">
									<RadioTower className="h-5 w-5 text-white" />
									<CardTitle className="text-lg font-bold text-white">Events</CardTitle>
								</div>
								<CardDescription className="text-xs px-4 pb-2 text-white">
									Network events.
								</CardDescription>
							</CardHeader>
							<CardContent className="px-2">
								<Events />
							</CardContent>
						</Card>
					</div>
				</div>
			</div>
		</>
	)
}

export {
	EventsExplorePage
}